/**
 * 手当（同伴・指名・ボトルバック等） v4.1.0
 *
 * これまで手当は NightPro（Laravel）側にしか無く、
 * 勤怠・シフト・ドリンクバック・指名は Firestore 側にあった。
 * 同じキャストが2つの台帳に存在し、2回打刻し、2つの給与表に載っていたため、
 * どちらを見ても支給額が出せなかった。
 *
 * 手当を Firestore 側に持たせて、給与を1本にする。
 *
 * 【手当とは】
 * 卓（会計）に対して付ける、商品ではない売上。
 *   同伴 / 本指名 / 場内指名 / ボトルバック / イベント指名 など。
 * 店が客から取る額（amount）と、キャストへ渡る率（backRate）を別に持つ。
 */

/** 手当の計算方法 */
export type AllowanceCalcType = 'fixed' | 'percent';

export interface AllowanceType {
  id: string;
  shopId: string;
  name: string;
  /** fixed = 定額 / percent = 会計小計に対する割合 */
  calcType: AllowanceCalcType;
  /** calcType='fixed' なら円、'percent' なら 0-1 の割合 */
  amount: number;
  /** キャストへのバック率（0-1）。店が取る額のうち何割をキャストへ渡すか */
  backRate: number;
  /** レシートに項目として印字するか */
  showOnReceipt: boolean;
  /** 会計の売上（＝分配やインセンティブの母数）に含めるか。
   *  同伴料のように「売上として立てる」ものは true、
   *  純粋なキャストへの報奨は false にして母数から外す。 */
  includeInSales: boolean;
  isActive: boolean;
  sortOrder: number;
  /** NightPro から取り込んだ場合の元ID。二重取り込みを防ぐ */
  nightProId?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

/** 会計に実際に付けられた手当 */
export interface AppliedAllowance {
  allowanceTypeId: string;
  name: string;
  calcType: AllowanceCalcType;
  /** 1件あたりの金額（percent の場合は計算後の円） */
  unitAmount: number;
  quantity: number;
  /** 客に請求する合計 */
  amount: number;
  /** キャストへ渡るバック */
  backAmount: number;
  backRate: number;
  /** 誰に付くか。未指定なら店の取り分 */
  staffId?: string;
  staffName?: string;
  includeInSales: boolean;
  showOnReceipt: boolean;
  note?: string;
}

export interface AllowanceInput {
  allowanceTypeId: string;
  quantity: number;
  staffId?: string;
  staffName?: string;
  note?: string;
}

/**
 * 手当の金額とバックを確定する。
 *
 * @param baseForPercent percent 手当の母数（会計の小計）
 */
export function resolveAllowance(
  type: AllowanceType,
  input: AllowanceInput,
  baseForPercent: number
): AppliedAllowance {
  const qty = Math.max(1, Math.floor(input.quantity || 1));
  const unit = type.calcType === 'percent'
    ? Math.round(baseForPercent * type.amount)
    : Math.round(type.amount);
  const amount = unit * qty;
  // Firestore は undefined を書き込めない。
  // 任意項目は「値がある時だけ入れる」形にして、保存側で毎回気にしなくて済むようにする。
  return {
    allowanceTypeId: type.id,
    name: type.name,
    calcType: type.calcType,
    unitAmount: unit,
    quantity: qty,
    amount,
    backRate: type.backRate,
    backAmount: Math.round(amount * type.backRate),
    includeInSales: type.includeInSales,
    showOnReceipt: type.showOnReceipt,
    ...(input.staffId ? { staffId: input.staffId } : {}),
    ...(input.staffName ? { staffName: input.staffName } : {}),
    ...(input.note ? { note: input.note } : {}),
  };
}

export function resolveAllowances(
  types: AllowanceType[],
  inputs: AllowanceInput[],
  baseForPercent: number
): AppliedAllowance[] {
  const byId = new Map(types.map((t) => [t.id, t]));
  const out: AppliedAllowance[] = [];
  for (const input of inputs) {
    const t = byId.get(input.allowanceTypeId);
    if (!t || !t.isActive) continue;
    out.push(resolveAllowance(t, input, baseForPercent));
  }
  return out;
}

export interface AllowanceSummary {
  /** 客に請求する手当の合計 */
  chargeTotal: number;
  /** 売上（分配の母数）に含める手当の合計 */
  salesTotal: number;
  /** キャストへ渡るバックの合計 */
  backTotal: number;
  /** スタッフID別のバック */
  backByStaff: Map<string, { staffName: string; amount: number; items: string[] }>;
  /** 担当が付いていない手当のバック（店に残る） */
  unassignedBack: number;
}

export function summarizeAllowances(applied: AppliedAllowance[]): AllowanceSummary {
  const backByStaff = new Map<string, { staffName: string; amount: number; items: string[] }>();
  let chargeTotal = 0;
  let salesTotal = 0;
  let backTotal = 0;
  let unassignedBack = 0;

  for (const a of applied) {
    chargeTotal += a.amount;
    if (a.includeInSales) salesTotal += a.amount;
    backTotal += a.backAmount;
    if (a.backAmount <= 0) continue;
    if (!a.staffId) {
      unassignedBack += a.backAmount;
      continue;
    }
    const cur = backByStaff.get(a.staffId) ?? { staffName: a.staffName ?? '', amount: 0, items: [] };
    cur.amount += a.backAmount;
    cur.items.push(`${a.name}${a.quantity > 1 ? `×${a.quantity}` : ''}`);
    if (!cur.staffName && a.staffName) cur.staffName = a.staffName;
    backByStaff.set(a.staffId, cur);
  }

  return { chargeTotal, salesTotal, backTotal, backByStaff, unassignedBack };
}

/** 画面に出す説明 */
export function describeAllowance(a: AppliedAllowance): string {
  const unit = a.calcType === 'percent'
    ? `小計の${Math.round(a.unitAmount)}円相当`
    : `¥${a.unitAmount.toLocaleString()}`;
  const who = a.staffName ? `${a.staffName}へ` : '担当なし（店）';
  return `${a.name} ${unit}${a.quantity > 1 ? ` ×${a.quantity}` : ''} = ¥${a.amount.toLocaleString()} / バック ¥${a.backAmount.toLocaleString()}（${who}）`;
}

/** 新規店舗に入れる既定の手当。夜職で最低限これだけあれば回る。 */
export const DEFAULT_ALLOWANCE_TYPES: Omit<AllowanceType, 'id' | 'shopId'>[] = [
  { name: '本指名', calcType: 'fixed', amount: 3000, backRate: 0.5, showOnReceipt: true, includeInSales: true, isActive: true, sortOrder: 1 },
  { name: '場内指名', calcType: 'fixed', amount: 2000, backRate: 0.5, showOnReceipt: true, includeInSales: true, isActive: true, sortOrder: 2 },
  { name: '同伴', calcType: 'fixed', amount: 5000, backRate: 0.5, showOnReceipt: true, includeInSales: true, isActive: true, sortOrder: 3 },
  { name: 'ボトルバック', calcType: 'percent', amount: 0.1, backRate: 1.0, showOnReceipt: false, includeInSales: false, isActive: true, sortOrder: 4 },
  { name: 'イベント指名', calcType: 'fixed', amount: 5000, backRate: 0.6, showOnReceipt: true, includeInSales: true, isActive: true, sortOrder: 5 },
];
